import type { Editor, TLRecord } from '@digitalsamba/tldraw';
import * as Sentry from '@sentry/react';
import { formatErrorForLogging, logger } from 'utils/logger';
import { lazySingleton } from 'utils/lazySingleton';
import { DRAFTS_LOG, DRAFTS_TAG } from './draftsLog';
import { ASSETS_STORE, DRAFTS_STORE, openDraftDb, probeWrite, RECORDS_STORE } from './draftDb';
import { WhiteboardDraft, WhiteboardDraftAsset, WhiteboardDraftRecord } from './types';

type SlotOwner = Pick<WhiteboardDraft, 'whiteboardId' | 'name' | 'isPrivate'>;
type DraftPatch = Partial<Omit<WhiteboardDraft, 'roomKey'>>;

const FLUSH_INTERVAL_MS = 2000;

export const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const idbRequest = <T>(req: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const commit = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

// Returns null rather than throwing: a malformed data: URL must cost one asset, not the whole
// batch of strokes queued alongside it.
const dataUrlToBytes = (dataUrl: string) => {
  try {
    const [header, encoded] = dataUrl.split(',');

    if (encoded === undefined || !header.includes(';base64')) {
      return null;
    }

    const mimeType = header.match(/data:([^;]+)/)?.[1] ?? 'application/octet-stream';
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return { data: bytes.buffer, mimeType };
  } catch (error) {
    logger.remote(DRAFTS_LOG).error('[drafts] Image source could not be decoded', {
      header: dataUrl.slice(0, 32),
      length: dataUrl.length,
      error: formatErrorForLogging(error),
    });

    Sentry.captureException(error, {
      tags: { feature: DRAFTS_TAG, operation: 'decodeAsset' },
      extra: { header: dataUrl.slice(0, 32), length: dataUrl.length },
    });

    return null;
  }
};

type ContentKeys = { records: IDBValidKey[]; assets: IDBValidKey[] };

type PendingBatch = { queued: TLRecord[]; deletes: string[] };

const CONTENT_TYPES = ['shape', 'asset'];

const isContentRecord = (record: TLRecord) => CONTENT_TYPES.includes(record.typeName);

type AssetLikeRecord = TLRecord & {
  props?: { src?: string; name?: string; mimeType?: string };
};

class WhiteboardDraftStore {
  private db: IDBDatabase | null = null;

  private roomKey = '';

  // The slot as last written. Keeping it here lets a flush write the row in the same transaction
  // as the content it describes, instead of reading it back first.
  private slot: WhiteboardDraft | null = null;

  private attached = false;

  private unlisten: (() => void) | null = null;

  private timer: ReturnType<typeof setInterval> | null = null;

  private pendingPut = new Map<string, TLRecord>();

  private pendingDelete = new Set<string>();

  // A failed commit puts the same record objects back on the queue, so without this a buffered
  // image is decoded again every couple of seconds. Entries go as soon as the bytes are stored:
  // the records stay reachable from the editor all session, so nothing here is freed on its own.
  private assetBytes = new WeakMap<TLRecord, ReturnType<typeof dataUrlToBytes>>();

  private flushFailing = false;

  private dropsReported = new Set<string>();

  // Resolved unless a claim is in flight, which is what separates it from `claim`: that one
  // stays pending until the board has earned the slot, and may never resolve at all.
  private ready: Promise<unknown> = Promise.resolve();

  // The board in view only becomes the room's board once it has something on it, so records
  // queued before that must not reach the slot the previous board still owns.
  private ownsSlot = false;

  private resolveClaim: ((draft: WhiteboardDraft | undefined) => void) | null = null;

  // Pending until this attach earns the slot, and resolved with undefined if it never does.
  // Teardown reads it to decide whether its own batch has anywhere to go.
  private claim: Promise<WhiteboardDraft | undefined> = Promise.resolve(undefined);

  // Taking the slot clears the room's content, so it has to wait for the outgoing board's last
  // write, or that write lands after the clear and leaves rows behind that nothing reads.
  private teardown: Promise<unknown> = Promise.resolve();

  init = async (roomKey: string) => {
    await this.close();

    this.roomKey = roomKey;

    try {
      const db = await openDraftDb();
      await probeWrite(db);

      this.db = db;

      return true;
    } catch (error) {
      this.db = null;

      // Not an exception: a private window and a blocked upgrade both land here and are expected.
      // The breadcrumb is what explains an absent restore row on the next report from this session.
      logger.remote(DRAFTS_LOG).warn('[drafts] Storage unavailable, drafts are off', {
        roomKey,
        error: formatErrorForLogging(error),
      });

      return false;
    }
  };

  // A breakout move re-runs init with a different room key, so the previous board must stop
  // writing and drop what it buffered before the key changes, or its content would be filed
  // under the new room.
  close = async () => {
    await this.detach();

    this.db?.close();
    this.db = null;

    this.roomKey = '';
    this.slot = null;
    this.assetBytes = new WeakMap();
    this.ownsSlot = false;
    this.flushFailing = false;
    this.dropsReported.clear();
    this.pendingPut.clear();
    this.pendingDelete.clear();
  };

  getDraft = async () => {
    if (!this.db) {
      return undefined;
    }

    const store = this.db.transaction(DRAFTS_STORE).objectStore(DRAFTS_STORE);

    return idbRequest<WhiteboardDraft | undefined>(
      store.get(this.roomKey) as IDBRequest<WhiteboardDraft | undefined>
    );
  };

  takeSlot = async (owner: SlotOwner, schema?: unknown) => {
    if (!this.db) {
      return undefined;
    }

    const existing = await this.getDraft();
    // A board can claim a slot it already owns — a later session, a re-attach after a rename —
    // and clearing then would delete the very records it is claiming with.
    const inherits = existing?.whiteboardId === owner.whiteboardId;

    if (existing && !inherits) {
      await this.dropContent(this.roomKey);
    }

    return this.writeDraft({
      roomKey: this.roomKey,
      whiteboardId: owner.whiteboardId,
      name: owner.name,
      isPrivate: owner.isPrivate,
      schema: schema ?? null,
      savedAt: Date.now(),
    });
  };

  patchDraft = async (patch: DraftPatch) => {
    const existing = await this.getDraft();

    // The slot was swept, or another tab handed it to a different board. Recreating it here
    // would resurrect a row with no content behind it.
    if (!existing) {
      return undefined;
    }

    return this.writeDraft({ ...existing, ...patch, roomKey: this.roomKey });
  };

  // Resolves with the slot once the board has earned it, or with undefined if it is detached
  // before that ever happens.
  attach = (editor: Editor, owner: SlotOwner) => {
    this.detach();

    this.attached = true;
    this.ownsSlot = false;

    this.claim = new Promise<WhiteboardDraft | undefined>((resolve) => {
      this.resolveClaim = resolve;
    });

    // Whatever is already on the board never reaches the listener: it was put there before the
    // mirror attached, by opening a board that was not empty.
    const existing = Object.values(editor.store.serialize()) as TLRecord[];

    existing.forEach((record) => this.queuePut(record));

    if (existing.some(isContentRecord)) {
      this.claimSlot(editor, owner);
    }

    // No source filter: BoardStateManager listens for 'user' only, so filtering here would
    // persist the local person's strokes and drop everyone else's.
    this.unlisten = editor.store.listen(
      (entry) => {
        const written = [
          ...(Object.values(entry.changes.added) as TLRecord[]),
          ...Object.values(entry.changes.updated).map(([, next]) => next as TLRecord),
        ];

        written.forEach((record) => this.queuePut(record));
        Object.values(entry.changes.removed).forEach((record) =>
          this.queueDelete(record as TLRecord)
        );

        if (this.resolveClaim && written.some(isContentRecord)) {
          this.claimSlot(editor, owner);
        }
      },
      { scope: 'document' }
    );

    this.timer = setInterval(this.flushNow, FLUSH_INTERVAL_MS);

    window.addEventListener('pagehide', this.flushNow);
    document.addEventListener('visibilitychange', this.flushOnHide);

    return this.claim;
  };

  private claimSlot = (editor: Editor, owner: SlotOwner) => {
    const resolve = this.resolveClaim;

    if (!resolve) {
      return;
    }

    // Claimed once per attach, and every flush waits on it so no record reaches the slot the
    // previous board still owns.
    this.resolveClaim = null;

    this.ready = this.teardown
      .then(() => this.takeSlot(owner, editor.store.schema.serialize()))
      .then((slot) => {
        if (!slot) {
          resolve(undefined);
          return;
        }

        this.ownsSlot = true;

        resolve(slot);
      })
      .catch((error) => {
        logger.remote(DRAFTS_LOG).error('[drafts] Board could not take the slot', {
          roomKey: this.roomKey,
          whiteboardId: owner.whiteboardId,
          error: formatErrorForLogging(error),
        });

        Sentry.captureException(error, {
          tags: { feature: DRAFTS_TAG, operation: 'takeSlot' },
          extra: { roomKey: this.roomKey, whiteboardId: owner.whiteboardId },
        });

        resolve(undefined);
      });
  };

  detach = () => {
    this.unlisten?.();
    this.unlisten = null;

    this.resolveClaim?.(undefined);
    this.resolveClaim = null;

    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    window.removeEventListener('pagehide', this.flushNow);
    document.removeEventListener('visibilitychange', this.flushOnHide);

    if (!this.attached) {
      return Promise.resolve();
    }

    this.attached = false;

    // Whatever is still buffered is the last couple of seconds of drawing; dropping it here
    // would lose exactly the work this feature exists to keep. Callers float this promise, so a
    // failure is reported rather than thrown. The batch is taken now and written after the claim
    // settles, or a board that has just earned the slot would lose the records that earned it.
    const pending = this.takePending();
    const claimed = this.claim;

    this.claim = Promise.resolve(undefined);

    this.teardown = claimed
      .then((slot) => (slot ? this.writePending(pending) : undefined))
      .catch(this.reportFlushFailure);

    return this.teardown;
  };

  // Rows outlive the board that wrote them until the next eviction, so a read that went by room
  // would hand a restore the records of a board the room has already moved on from.
  private slotBoardId = async () => {
    if (this.slot) {
      return this.slot.whiteboardId;
    }

    const draft = await this.getDraft();

    return draft?.whiteboardId;
  };

  readRecords = async () => {
    const whiteboardId = await this.slotBoardId();

    if (!this.db || !whiteboardId) {
      return [];
    }

    const store = this.db.transaction(RECORDS_STORE).objectStore(RECORDS_STORE);
    const rows = await idbRequest<WhiteboardDraftRecord[]>(
      store.index('by-board').getAll([this.roomKey, whiteboardId]) as IDBRequest<
        WhiteboardDraftRecord[]
      >
    );

    return rows.map((row) => row.record);
  };

  readAsset = async (assetId: string) => {
    const whiteboardId = await this.slotBoardId();

    if (!this.db || !whiteboardId) {
      return undefined;
    }

    const store = this.db.transaction(ASSETS_STORE).objectStore(ASSETS_STORE);

    return idbRequest<WhiteboardDraftAsset | undefined>(
      store.get([this.roomKey, whiteboardId, assetId]) as IDBRequest<
        WhiteboardDraftAsset | undefined
      >
    );
  };

  // The timer and the two teardown listeners call this without awaiting, so a rejection here
  // would surface as an unhandled rejection every couple of seconds.
  flushNow = async () => {
    await this.ready;

    if (!this.attached) {
      return;
    }

    try {
      await this.flushDraft();
    } catch (error) {
      this.reportFlushFailure(error);
    }
  };

  // A flush that fails once usually fails on every retry — a quota that is full stays full — and
  // the timer would report the same error every couple of seconds for the rest of the session.
  private reportFlushFailure = (error: unknown) => {
    if (this.flushFailing) {
      return;
    }

    this.flushFailing = true;

    const context = {
      roomKey: this.roomKey,
      whiteboardId: this.slot?.whiteboardId,
      queuedPuts: this.pendingPut.size,
      queuedDeletes: this.pendingDelete.size,
      ownsSlot: this.ownsSlot,
    };

    logger.remote(DRAFTS_LOG).error('[drafts] Write failed, nothing is being saved', {
      ...context,
      error: formatErrorForLogging(error),
    });

    Sentry.captureException(error, {
      tags: { feature: DRAFTS_TAG, operation: 'flush' },
      extra: context,
    });
  };

  // A refused record is offered again on every stroke, so only the ones never reported are worth a
  // report — the count is bounded by how many records the database refuses, which is a handful.
  private reportDroppedRecords = (dropped: string[]) => {
    const fresh = dropped.filter((recordId) => !this.dropsReported.has(recordId));

    if (!fresh.length) {
      return;
    }

    fresh.forEach((recordId) => this.dropsReported.add(recordId));

    const context = {
      roomKey: this.roomKey,
      whiteboardId: this.slot?.whiteboardId,
      dropped: fresh.length,
      recordIds: fresh.slice(0, 10),
    };

    logger
      .remote(DRAFTS_LOG)
      .error('[drafts] Storage refused a record, it is not being saved', context);

    Sentry.captureException(new Error(`Whiteboard draft dropped ${fresh.length} record(s)`), {
      tags: { feature: DRAFTS_TAG, operation: 'writeRecord' },
      extra: context,
    });
  };

  sweep = async () => {
    if (!this.db) {
      return;
    }

    const all = await this.listAllDrafts();
    const expiry = Date.now() - DRAFT_TTL_MS;

    // Evicting this room's slot while it is attached would delete the board the person is
    // drawing on right now.
    const held = this.attached ? this.roomKey : null;

    const doomed = all
      .filter((entry) => entry.savedAt < expiry)
      .filter((entry) => entry.roomKey !== held)
      .map((entry) => entry.roomKey);

    await Promise.all(doomed.map((roomKey) => this.dropRoom(roomKey)));
  };

  private writeDraft = async (draft: WhiteboardDraft) => {
    if (!this.db) {
      return undefined;
    }

    const tx = this.db.transaction(DRAFTS_STORE, 'readwrite');
    tx.objectStore(DRAFTS_STORE).put(draft);
    await commit(tx);

    this.slot = draft;

    return draft;
  };

  // An E2EE image record carries its bytes as base64 in props.src for the local editor, next to
  // the encrypted upload's URL in meta.src. Only the bytes are worth keeping, and not as a string
  // inside a record that is rewritten on every stroke.
  private splitAsset = (record: TLRecord, whiteboardId: string) => {
    const asset = record as AssetLikeRecord;

    if (record.typeName !== 'asset' || !asset.props?.src?.startsWith('data:')) {
      return { record, asset: null };
    }

    const bytes = this.decodeOnce(record, asset.props.src);

    if (!bytes) {
      return { record, asset: null };
    }

    return {
      record: { ...asset, props: { ...asset.props, src: '' } } as TLRecord,
      asset: {
        roomKey: this.roomKey,
        whiteboardId,
        assetId: String(record.id),
        data: bytes.data,
        name: asset.props.name ?? '',
        mimeType: asset.props.mimeType ?? bytes.mimeType,
      } satisfies WhiteboardDraftAsset,
    };
  };

  private decodeOnce = (record: TLRecord, src: string) => {
    const cached = this.assetBytes.get(record);

    if (cached !== undefined) {
      return cached;
    }

    const bytes = dataUrlToBytes(src);

    this.assetBytes.set(record, bytes);

    return bytes;
  };

  // Taken separately from the write so that teardown owns its batch before the next board can
  // queue anything: the two share one buffer.
  private takePending = (): PendingBatch => {
    const queued = Array.from(this.pendingPut.values());
    const deletes = Array.from(this.pendingDelete);

    this.pendingPut.clear();
    this.pendingDelete.clear();

    return { queued, deletes };
  };

  // Guarded before the take, not inside writePending: takePending empties the buffer, so a write
  // that then declined to run would drop the batch instead of leaving it for the next flush.
  private flushDraft = async () => {
    if (!this.db || !this.slot || !this.ownsSlot) {
      return;
    }

    if (!this.pendingPut.size && !this.pendingDelete.size) {
      return;
    }

    await this.writePending(this.takePending());
  };

  // Callers answer for ownership: the timer through ownsSlot, teardown through the slot its own
  // board earned. The row itself always comes from the store, so a rename is never written back.
  private writePending = async ({ queued, deletes }: PendingBatch) => {
    if (!this.db || !this.slot || (!queued.length && !deletes.length)) {
      return;
    }

    // The listener keeps firing while the commit is awaited, so anything already queued again is
    // newer than what this flush captured and must win.
    const requeue = () => {
      queued.forEach((record) => {
        const id = String(record.id);

        if (!this.pendingPut.has(id) && !this.pendingDelete.has(id)) {
          this.pendingPut.set(id, record);
        }
      });
      deletes.forEach((recordId) => {
        if (!this.pendingPut.has(recordId)) {
          this.pendingDelete.add(recordId);
        }
      });
    };

    const { whiteboardId } = this.slot;

    const split = queued.map((record) => this.splitAsset(record, whiteboardId));
    const puts = split.map((entry) => entry.record);
    const assets = split
      .map((entry) => entry.asset)
      .filter((asset): asset is WhiteboardDraftAsset => asset !== null);

    const slot: WhiteboardDraft = { ...this.slot, savedAt: Date.now() };

    const tx = this.db.transaction([RECORDS_STORE, ASSETS_STORE, DRAFTS_STORE], 'readwrite');
    const records = tx.objectStore(RECORDS_STORE);
    const assetStore = tx.objectStore(ASSETS_STORE);

    const dropped: string[] = [];

    puts.forEach((record) => {
      try {
        records.put({
          roomKey: this.roomKey,
          whiteboardId,
          recordId: String(record.id),
          record,
        } satisfies WhiteboardDraftRecord);
      } catch {
        dropped.push(String(record.id));
      }
    });

    deletes.forEach((recordId) => {
      records.delete([this.roomKey, whiteboardId, recordId]);
      assetStore.delete([this.roomKey, whiteboardId, recordId]);
    });

    assets.forEach((asset) => {
      try {
        assetStore.put(asset);
      } catch {
        dropped.push(asset.assetId);
      }
    });

    tx.objectStore(DRAFTS_STORE).put(slot);

    if (dropped.length) {
      this.reportDroppedRecords(dropped);
    }

    try {
      await commit(tx);
    } catch (error) {
      // Nothing landed, so put the records back rather than reporting a save that did not happen;
      // the next flush retries them.
      requeue();

      throw error;
    }

    queued.forEach((record) => this.assetBytes.delete(record));

    this.slot = slot;
    this.flushFailing = false;
  };

  private listAllDrafts = async () => {
    if (!this.db) {
      return [];
    }

    const store = this.db.transaction(DRAFTS_STORE).objectStore(DRAFTS_STORE);

    return idbRequest<WhiteboardDraft[]>(store.getAll() as IDBRequest<WhiteboardDraft[]>);
  };

  private contentKeys = async (roomKey: string): Promise<ContentKeys> => {
    if (!this.db) {
      return { records: [], assets: [] };
    }

    const tx = this.db.transaction([RECORDS_STORE, ASSETS_STORE]);

    const [records, assets] = await Promise.all([
      idbRequest<IDBValidKey[]>(
        tx.objectStore(RECORDS_STORE).index('by-roomKey').getAllKeys(roomKey)
      ),
      idbRequest<IDBValidKey[]>(
        tx.objectStore(ASSETS_STORE).index('by-roomKey').getAllKeys(roomKey)
      ),
    ]);

    return { records, assets };
  };

  private deleteContent = (tx: IDBTransaction, keys: ContentKeys) => {
    keys.records.forEach((key) => tx.objectStore(RECORDS_STORE).delete(key));
    keys.assets.forEach((key) => tx.objectStore(ASSETS_STORE).delete(key));
  };

  private dropContent = async (roomKey: string) => {
    if (!this.db) {
      return;
    }

    const keys = await this.contentKeys(roomKey);
    const tx = this.db.transaction([RECORDS_STORE, ASSETS_STORE], 'readwrite');

    this.deleteContent(tx, keys);

    await commit(tx);
  };

  private dropRoom = async (roomKey: string) => {
    if (!this.db) {
      return;
    }

    const keys = await this.contentKeys(roomKey);

    // One transaction over all three stores: a half-applied eviction would leave content that
    // nothing enumerates any more, since sweep only ever walks the drafts store.
    const tx = this.db.transaction([RECORDS_STORE, ASSETS_STORE, DRAFTS_STORE], 'readwrite');

    this.deleteContent(tx, keys);
    tx.objectStore(DRAFTS_STORE).delete(roomKey);

    await commit(tx);

    if (roomKey === this.roomKey) {
      this.slot = null;
    }
  };

  private flushOnHide = () => {
    if (document.visibilityState === 'hidden') {
      this.flushNow();
    }
  };

  private queuePut = (record: TLRecord) => {
    const id = String(record.id);

    this.pendingDelete.delete(id);
    this.pendingPut.set(id, record);
  };

  private queueDelete = (record: TLRecord) => {
    const id = String(record.id);

    this.pendingPut.delete(id);
    this.pendingDelete.add(id);
  };
}

export const whiteboardDrafts = lazySingleton<WhiteboardDraftStore>(
  () => new WhiteboardDraftStore()
);
